/**
 * A connector's pinout: every pin it has, and what each one carries (issue #52).
 *
 * The pins come from the connector TYPE - an RJ45 declares eight - and every
 * port def built on it inherits them. What changes down the chain is the
 * FUNCTION: an EthernetPort built on RJ45 says pin 1 is TX+. So each row says
 * where its function came from, and an edit here is written on THIS port def,
 * never on the one it inherits from.
 *
 * A function that matches what is inherited is not written again; an emptied
 * function this def assigned falls back to the inherited one, or to a spare.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { Pinout } from '../../schema'
import { useCanWrite } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'

export default function PinoutEditor({ identifier }: { identifier: string }) {
  const permission = useCanWrite()
  const toasts = useToasts()
  const [pinout, setPinout] = useState<Pinout | null>(null)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  useEffect(() => {
    let live = true
    setPinout(null)
    api.pinout(identifier)
      .then((found) => {
        if (!live) return
        setPinout(found)
        setDrafts(Object.fromEntries(found.pins.map((pin) => [pin.name, pin.function])))
      })
      .catch((error: ApiError) => live && setFailure(error.message))
    return () => {
      live = false
    }
  }, [identifier])

  if (failure && !pinout) return <Alert severity="error">{failure}</Alert>
  if (!pinout) return <CircularProgress size={20} />
  if (pinout.pins.length === 0) return null

  const changed = pinout.pins.filter((pin) => (drafts[pin.name] ?? '') !== pin.function)

  const save = async () => {
    setBusy(true)
    setFailure('')
    try {
      const answer = await api.setPinout(
        identifier,
        changed.map((pin) => ({ name: pin.name, function: (drafts[pin.name] ?? '').trim() })),
      )
      setPinout(answer)
      setDrafts(Object.fromEntries(answer.pins.map((pin) => [pin.name, pin.function])))
      toasts.succeeded(`Pinout saved: ${answer.changed.join(', ') || 'nothing changed'}`)
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack spacing={1}>
      <Divider />
      <Box>
        <Typography variant="subtitle2">Pinout</Typography>
        <Typography variant="caption" color="text.secondary">
          {pinout.connector_type ? `${pinout.connector_type}, ` : ''}
          {pinout.pins.length} pins
          {pinout.chain.length > 1 ? ` — built on ${pinout.chain.slice(1).join(' ← ')}` : ''}. An empty
          function is a spare.
        </Typography>
      </Box>
      {failure && <Alert severity="error" onClose={() => setFailure('')}>{failure}</Alert>}
      <Table size="small" aria-label="Pinout">
        <TableHead>
          <TableRow>
            <TableCell>Pin</TableCell>
            <TableCell>Function</TableCell>
            <TableCell>Assigned on</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pinout.pins.map((pin) => (
            <TableRow key={pin.name}>
              <TableCell sx={{ fontFamily: MONOSPACE, width: 90 }}>{pin.name}</TableCell>
              <TableCell>
                <TextField
                  size="small"
                  value={drafts[pin.name] ?? ''}
                  onChange={(event) => setDrafts((current) => ({ ...current, [pin.name]: event.target.value }))}
                  placeholder="spare"
                  disabled={!permission.allowed || busy}
                  slotProps={{ htmlInput: { 'aria-label': `Function of ${pin.name}`, style: { fontFamily: MONOSPACE } } }}
                  fullWidth
                />
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                {pin.function_from ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box>
        <Tooltip title={permission.allowed ? '' : permission.reason} describeChild>
          <span>
            <Button
              size="small"
              variant="contained"
              disabled={!permission.allowed || busy || changed.length === 0}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : changed.length ? `Save ${changed.length} pin(s)` : 'Save pinout'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Stack>
  )
}
